// ========= IMPORTS ==========

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/Api";
import { useCommon } from "../context/CommonContext";
import Loading from "./Loading";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "../context/LanguageProvider";


// ========= CUSTOM HOOK ==========

// Encapsulates the logic for fetching survey instructions
// Accepts surveyId, returns useQuery's status/data/error handling via react-query
function useInstructions(surveyId) {
  return useQuery({
    queryKey: ["instructions", surveyId],           // Unique cache key based on survey ID
    queryFn: () => api.fetchInstructions(surveyId) // API function returning instructions list
  });
}


// ========= MAIN COMPONENT ==========

const GeneralInstructions = () => {

  // Debug log to see in console when this component renders
  console.log("------------------generalinstructions rendered----------------------");

  // ===== Context hooks =====
  const { surveyId, nextButtonText, beginButtonText } = useCommon();
  const { translation } = useLanguage();

  // ===== Data fetching via custom hook =====
  const {
    data: initialData, // the result of the API, once loaded
    status,            // "pending" | "success" | "error" — from react-query
    error,             // potential error object from API call
    fetchStatus        // react-query network status ("fetching", "paused", "idle")
  } = useInstructions(surveyId);

  // ===== Local State =====
  // currentPage tracks which page of instructions the user is currently reading
  const [currentPage, setCurrentPage] = useState(0);

  // Router navigation function
  const navigate = useNavigate();

  // ===== Instructions array setup =====
  let instructions = [];
  if (status === "success") {
    // If data is successfully fetched, store it or use empty array if null
    instructions = initialData || [];
  } else if (status === "error" && error.message === "Error: 404") {
    // Special case: API says "not found" → treat as no instructions
    instructions = [];
  }

  // ===== Handlers =====

  // Called each time user clicks "Next" or "Begin"
  // If there are more instruction pages, move forward
  // If last page reached, move to IPGI survey section
  const handleNextClick = () => {
    if (currentPage < instructions.length - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      navigate("/IPGI");
    }
  };

  // ===== Loading State Handling =====
  if (fetchStatus === "fetching" || status === "pending") {
    return <Loading />; // Show spinner/loading until data arrives
  }

  // ===== Error State Handling =====
  if (status === "error" && error.message !== "Error: 404") {
    // If the error message starts with "Error:", it's an API error
    if (error.message.startsWith("Error:")) {
      return <div>{translation("Error")} {error.message} </div>;
    }
    // Otherwise show network error fallback
    return <div>{translation("Network_error")}</div>;
  }

  // ===== Identify current instruction object =====
  const currentInstruction = instructions.find(
    (instr) => instr.instruction_page === currentPage
  );

  // ===== Render =====
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      
      {instructions.length === 0 ? (
        // Case: No instructions to display
        <div>
          <button
            onClick={() => navigate("/IPGI")} // go straight to next survey phase
            className="px-6 py-2 bg-black text-white text-xl font-semibold rounded-lg shadow hover:bg-gray-800 transition duration-200 mt-8"
          >
            {beginButtonText.current}
          </button>
        </div>
      ) : (
        // Case: Show current instruction + navigation button
        <>
          {/* Instruction content area 
              - Uses dangerouslySetInnerHTML to inject HTML from server response
              - Must ensure content is sanitized/trusted (security concern: XSS)
          */}
          <div
            className="text-3xl text-gray-800 text-center font-normal max-w-full"
            dangerouslySetInnerHTML={{ __html: currentInstruction?.instruction }}
          />

          {/* Next / Begin button — click advances page or moves to IPGI */}
          <div className="flex items-center">
            <button
              onClick={handleNextClick}
              className="px-6 py-2 bg-black text-white text-xl font-semibold rounded-lg shadow hover:bg-gray-800 transition duration-200 mt-8"
            >
              {currentPage === instructions.length - 1
                ? beginButtonText.current
                : nextButtonText.current
              }
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GeneralInstructions;
